<template>
  <v-container fluid class="pa-1">
    <template v-if="Array.isArray(itemId)">
      <v-row dense>
        <v-toolbar color="secondary" dark dense>
          <v-toolbar-title class="pa-3"> Forecast Id </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-col sm="3" class="pt-6">
            <v-select
              v-model="outputId"
              :items="itemId"
              label="Select ID"
              dense
              @change="$emit('change')"
              hide-details
            />
          </v-col>
        </v-toolbar>
      </v-row>
    </template>
    <v-row dense>
      <v-col cols v-for="(chart, i) in charts" :key="`chart-${chart.title}-${i}`">
        <v-toolbar flat>
          <v-toolbar-title>
            {{ chart.title }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-col sm="3" class="pt-6">
            <v-select
              v-model="chartItems[i].granularity"
              :items="granularityItems"
              dense
              hide-details
            >
            </v-select>
          </v-col>
        </v-toolbar>
        <v-card flat>
          <v-progress-circular
            v-if="displayLoader(i)"
            color="secondary"
            :indeterminate="true"
            absolute
            class="chart-loader"
            size="24"
            width="3"
          ></v-progress-circular>
          <Plotly :data="chart.data" :layout="chart.layout" />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapMutations } from "vuex";

export default {
  name: "ForecastOutputCharts",
  components: {
    Plotly: () => import("@/components/charts/Plotly.vue"),
  },
  mixins: [],
  props: {
    itemId: { type: [Number, Array], required: true },
  },
  data() {
    return {
      chartItems: [
        {
          title: "Forecasted Energy Prices",
          outputType: "energy_prices",
          chartData: "energyChartData",
          granularity: "annual",
        },
        {
          title: "Forecasted Reserves",
          outputType: "reserves",
          chartData: "reservesChartData",
          granularity: "annual",
        },
      ],
      outputId: Array.isArray(this.itemId) ? Math.max(...this.itemId) : this.itemId,
      granularityOrder: ["annual", "monthly", "weekly", "daily", "hourly"],
      forecastOutput: { annual: [], monthly: [], weekly: [], daily: [], hourly: [] },
      showReserves: true,
    };
  },
  computed: {
    computedForecastId: {
      get() {
        return this.outputId;
      },
      set(val) {
        this.$emit("update:output-id", val);
        this.$emit("change");
      },
    },
    granularityItems() {
      return Object.keys(this.forecastOutput)
        .map((v) => ({
          text: v.toLowerCase().replace(/\b[a-zA-Z]/g, (word) => word.toUpperCase()),
          value: v,
        }))
        .sort(
          (a, b) =>
            this.granularityOrder.findIndex((item) => item === a.value) -
            this.granularityOrder.findIndex((item) => item === b.value)
        );
    },
    charts() {
      return this.chartItems
        .filter((chart) => {
          if (chart.outputType === "reserves") {
            return this.showReserves;
          }
          return true;
        })
        .map((v) => {
          return {
            ...v,
            layout: {
              title: v.title,
              xaxis: {
                autorange: true,
                rangeslider: {},
                type: "date",
              },
              yaxis: {
                title: "$/MWh",
                fixedrange: false,
              },
              autosize: true,
            },
            data: this[v.chartData],
          };
        });
    },
    energyChartData() {
      const { granularity } = this.chartItems.find((el) => el.outputType === "energy_prices");
      if (this.forecastOutput[granularity]) {
        return [
          {
            x: this.forecastOutput[granularity].map((v) => v.timestamp),
            y: this.forecastOutput[granularity].map((v) => v.energy_price.high),
            name: "High",
          },
          {
            x: this.forecastOutput[granularity].map((v) => v.timestamp),
            y: this.forecastOutput[granularity].map((v) => v.energy_price.base),
            name: "Base",
          },
          {
            x: this.forecastOutput[granularity].map((v) => v.timestamp),
            y: this.forecastOutput[granularity].map((v) => v.energy_price.low),
            name: "Low",
          },
        ];
      }
      return [];
    },
    reservesChartData() {
      const data = [];
      const reservesData = {};
      const { granularity } = this.chartItems.find((el) => el.outputType === "reserves");
      if (this.forecastOutput[granularity].length) {
        Object.entries(this.forecastOutput[granularity][0].reserves).forEach((reserve) => {
          reservesData[reserve[0]] = [];
        });
        this.forecastOutput[granularity].forEach((element) => {
          Object.entries(element.reserves).forEach((reserve) => {
            reservesData[reserve[0]].push({
              timestamp: element.timestamp,
              ...reserve[1],
            });
          });
        });

        Object.entries(reservesData).forEach((element) => {
          data.push(
            {
              x: element[1].map((v) => v.timestamp),
              y: element[1].map((v) => v.low),
              name: `${element[0].replace(/\b[a-zA-Z]/g, (word) => word.toUpperCase())} - Low`,
            },
            {
              x: element[1].map((v) => v.timestamp),
              y: element[1].map((v) => v.base),
              name: `${element[0].replace(/\b[a-zA-Z]/g, (word) => word.toUpperCase())} - Base`,
            },
            {
              x: element[1].map((v) => v.timestamp),
              y: element[1].map((v) => v.high),
              name: `${element[0].replace(/\b[a-zA-Z]/g, (word) => word.toUpperCase())} - High`,
            }
          );
        });
        return data.sort((a, b) => (a.name.split(" -")[0] > b.name.split(" -")[0] ? 1 : -1));
      }
      return data;
    },
  },
  watch: {
    computedForecastId() {
      this.showReserves = true;
      this.chartItems = this.chartItems.map((chart) => ({
        ...chart,
        granularity: "annual",
      }));
      this.loadOutputs();
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {
    this.loadOutputs();
  },
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    ...mapActions({
      getOutput: "forecasts/get",
    }),
    ...mapMutations({
      clearStore: "forecasts/removeItem",
    }),
    loadOutputs() {
      if (this.computedForecastId) {
        this.getOutput([
          this.computedForecastId,
          {
            query: { $select: ["id", "input", "ref(output:annual) as annual"] },
          },
        ]).then((r) => {
          if (r.input.energy_ancillary_inputs.ancillary_settings) {
            this.showReserves = r.input.energy_ancillary_inputs.ancillary_settings.active;
          }
          if (r.annual) {
            this.forecastOutput.annual = r.annual;

            // Get monthly data
            this.getOutput([
              this.computedForecastId,
              {
                query: { $select: ["id", "ref(output:monthly) as monthly"] },
              },
            ]).then((rMonthly) => {
              if (rMonthly.monthly) {
                this.forecastOutput.monthly = rMonthly.monthly;
                this.chartItems = this.chartItems.map((chart) => ({
                  ...chart,
                  granularity: "monthly",
                }));

                // Get weeekly data
                this.getOutput([
                  this.computedForecastId,
                  {
                    query: { $select: ["id", "ref(output:weekly) as weekly"] },
                  },
                ]).then((rWeekly) => {
                  if (rWeekly.weekly) {
                    this.forecastOutput.weekly = rWeekly.weekly;
                    this.chartItems = this.chartItems.map((chart) => ({
                      ...chart,
                      granularity: "weekly",
                    }));

                    // Get daily data
                    this.getOutput([
                      this.computedForecastId,
                      {
                        query: { $select: ["id", "ref(output:daily) as daily"] },
                      },
                    ]).then((rDaily) => {
                      if (rDaily.daily) {
                        this.forecastOutput.daily = rDaily.daily;

                        // Get hourly data
                        this.getOutput([
                          this.computedForecastId,
                          {
                            query: { $select: ["id", "ref(output:hourly) as hourly"] },
                          },
                        ]).then((rHourly) => {
                          if (rHourly.hourly) {
                            this.forecastOutput.hourly = rHourly.hourly;
                          }
                        });
                      }
                    });
                  }
                });
              }
            });
          }
        });
      }
    },
    displayLoader(chartIndex) {
      return Boolean(!this.forecastOutput[this.chartItems[chartIndex].granularity].length);
    },
  },
};
</script>

<style>
.chart-loader {
  position: absolute;
  opacity: 1 !important;
  top: 40%;
  left: 50%;
  z-index: 4;
}
</style>
